import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PenLine, Mail, Phone, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import { newsApi } from '../utils/api';
import { useSiteSettings } from '../hooks/useSiteSettings';
import NewsList from '../components/common/NewsList';
import SectionHeading from '../components/common/SectionHeading';
import AuthorAvatar from '../components/common/AuthorAvatar';
import PageLoader from '../components/common/PageLoader';

const toArray = (v) => (Array.isArray(v) ? v : []);

function AuthorProfilePage({ onOpen }) {
  const { author } = useParams();
  const settings = useSiteSettings();
  const decodedName = decodeURIComponent(author || '');
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let mounted = true;
    const fetchNews = async () => {
      setLoading(true);
      try {
        const res = await newsApi.getByAuthor(decodedName, { limit: 60 });
        if (mounted) setNews(toArray(res.data) || []);
      } catch (e) {
        if (mounted) setNotFound(true);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    if (decodedName) fetchNews();
    else setLoading(false);
    return () => { mounted = false; };
  }, [decodedName]);

  // Team members bata profile details khojne — match by name
  let team = [];
  try {
    const arr = JSON.parse(settings.team_members || '[]');
    if (Array.isArray(arr)) team = arr.filter((m) => m && String(m.name || '').trim());
  } catch {
    team = [];
  }
  const member = team.find((m) => (m.name || '').trim().toLowerCase() === decodedName.toLowerCase());

  const photo = member?.photo || news[0]?.author_image || null;
  const role = member?.role || null;
  const phone = member?.phone || null;
  const email = member?.email || null;

  return (
    <main className="page max-w-[1240px] mx-auto px-5 pt-7 pb-[60px] w-full">
      {/* Author profile header */}
      <motion.section
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="bg-white border border-line rounded-2xl shadow-sm p-6 sm:p-8 mb-8"
      >
        <div className="flex flex-col sm:flex-row items-center sm:items-center gap-5 sm:gap-7">
          {photo ? (
            <img src={photo} alt={decodedName} className="w-24 h-24 sm:w-32 sm:h-32 rounded-full object-cover border-2 border-line flex-shrink-0" />
          ) : (
            <AuthorAvatar name={decodedName} size={120} />
          )}
          <div className="text-center sm:text-left min-w-0">
            <h1 className="font-display text-[24px] sm:text-[30px] font-bold flex items-center justify-center sm:justify-start gap-2.5">
              <PenLine size={22} className="text-crimson" />
              {decodedName}
            </h1>
            {role && <p className="text-[13px] font-bold text-gold uppercase tracking-wide mt-1">{role}</p>}
            <p className="text-muted text-[14px] mt-2 leading-relaxed">
              {member ? `${decodedName} का समाचारहरू यहाँ हेर्नुहोस्।` : `"${decodedName}" को सम्पूर्ण समाचारहरू।`}
            </p>
            {(phone || email) && (
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-5 gap-y-1.5 mt-3">
                {phone && (
                  <a href={`tel:${phone}`} className="inline-flex items-center gap-1.5 text-[13px] text-blue hover:text-crimson transition-colors">
                    <Phone size={14} /> {phone}
                  </a>
                )}
                {email && (
                  <a href={`mailto:${email}`} className="inline-flex items-center gap-1.5 text-[13px] text-blue hover:text-crimson transition-colors break-all">
                    <Mail size={14} /> {email}
                  </a>
                )}
              </div>
            )}
          </div>
          <div className="sm:ml-auto flex flex-col items-center sm:items-end gap-1 flex-shrink-0">
            <span className="text-[12px] uppercase tracking-wide text-muted">कुल समाचार</span>
            <span className="font-display text-[34px] font-extrabold text-crimson leading-none">{news.length}</span>
          </div>
        </div>
      </motion.section>

      {/* Author's news list */}
      <motion.section
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut', delay: 0.1 }}
      >
        <SectionHeading title={`${decodedName} का समाचारहरू`} />
        {loading ? (
          <PageLoader label="समाचार लोड हुँदै..." />
        ) : notFound ? (
          <div className="text-center py-16">
            <Users size={48} className="mx-auto text-muted mb-4" />
            <h3 className="font-display text-xl font-bold text-ink mb-2">समाचार लोड गर्न सकिएन</h3>
            <p className="text-muted">फेरि प्रयास गर्नुहोस्।</p>
          </div>
        ) : news.length === 0 ? (
          <div className="text-center py-16">
            <PenLine size={48} className="mx-auto text-muted mb-4" />
            <h3 className="font-display text-xl font-bold text-ink mb-2">कुनै समाचार भेटिएन</h3>
            <p className="text-muted">{decodedName} का लागि अहिलेसम्म कुनै समाचार छैन।</p>
          </div>
        ) : (
          <NewsList news={news} view="list" onOpen={onOpen} />
        )}
      </motion.section>
    </main>
  );
}

export default AuthorProfilePage;
